/**
 * Shell — the desktop sidebar + top bar and the mobile app bar + 5-tab bottom navigation with
 * the "עוד" sheet (spec-business §3.1–§3.2), plus the global elements of §3.4: the period
 * picker, the freshness chip, the refresh dialog with its credits warning, the notifications
 * bell (a floating panel on desktop, `/alerts` on mobile) and the EXPIRED / stale banners.
 */

import type { ReactNode } from 'react';
import type { Alert, PeriodParam, SummaryResponse } from '../../../api-types.ts';
import { fmtDate, fmtDateTime, initial, mask, monthName, relTime } from '../format.ts';
import { addMonths, monthOf, periodChipLabel, periodMenuMonths } from '../dates.ts';
import { ALERT_ICON, he, providerName } from '../i18n/he.ts';
import { useApp } from '../hooks/useApp.tsx';
import { useIsMobile } from '../hooks/useMediaQuery.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath, type ScreenName } from '../router.ts';
import { ProviderMark } from '../../../../shared/ui/src/index.tsx';
import { Icon } from './Icon.tsx';
import { EmptyState, Mask, Num } from './ui.tsx';

const NAV: Array<{ screen: ScreenName; label: string; icon: string }> = [
  { screen: 'home', label: he.nav.home, icon: 'home' },
  { screen: 'cashflow', label: he.nav.cashflow, icon: 'activity' },
  { screen: 'invoices', label: he.nav.invoices, icon: 'file' },
  { screen: 'vat', label: he.nav.vat, icon: 'landmark' },
  { screen: 'expenses', label: he.nav.expenses, icon: 'bag' },
  { screen: 'loans', label: he.nav.loans, icon: 'landmark' },
  { screen: 'ops', label: he.nav.ops, icon: 'wrench' },
];

/** Exactly 5 tabs (spec §3.2): ראשי · גבייה · תזרים · מע"מ · עוד. */
const TABS: Array<{ screen: ScreenName; label: string; icon: string }> = [
  { screen: 'home', label: he.nav.home, icon: 'home' },
  { screen: 'invoices', label: he.nav.invoicesShort, icon: 'file' },
  { screen: 'cashflow', label: he.nav.cashflow, icon: 'activity' },
  { screen: 'vat', label: he.nav.vatShort, icon: 'landmark' },
];

const MORE_SCREENS: ReadonlySet<ScreenName> = new Set<ScreenName>(['expenses', 'loans', 'ops', 'alerts', 'settings']);
const PERIOD_SCREENS: ReadonlySet<ScreenName> = new Set<ScreenName>(['home', 'cashflow', 'expenses', 'ops']);

/** The screen's title, the mobile back link and the desktop subtitle (spec §3.1). */
export function screenTitle(screen: ScreenName): string {
  switch (screen) {
    case 'cashflow':
      return he.nav.cashflow;
    case 'invoices':
      return he.nav.invoices;
    case 'vat':
      return he.nav.vat;
    case 'expenses':
      return he.nav.expenses;
    case 'loans':
      return he.loans.title;
    case 'ops':
      return he.nav.ops;
    case 'alerts':
      return he.alerts.title;
    case 'settings':
      return he.settings.title;
    case 'login':
      return he.login.title;
    default:
      return he.nav.home;
  }
}

export function freshnessView(summary: SummaryResponse | undefined): { text: string; tone: '' | 'warn' | 'critical' } {
  if (!summary) return { text: '', tone: '' };
  const { freshness } = summary;
  if (freshness.level === 'none' || !freshness.lastFetchedAt) return { text: he.freshness.none, tone: 'critical' };
  if (freshness.level === 'stale_critical') return { text: he.freshness.critical(Math.floor((freshness.ageHours ?? 72) / 24)), tone: 'critical' };
  if (freshness.level === 'stale_warn') return { text: he.freshness.warn, tone: 'warn' };
  return { text: he.freshness.fresh(relTime(freshness.lastFetchedAt)), tone: '' };
}

/** Where an alert links to; the API supplies a path, and this is the fallback per type. */
export function alertTarget(alert: Alert): string {
  if (alert.link) return alert.link;
  switch (alert.type) {
    case 'INVOICE_OVERDUE':
      return buildPath('invoices', null, { status: 'overdue' });
    case 'VAT_DUE':
    case 'TAX_ADVANCE_DUE':
      return buildPath('vat');
    case 'EXPENSE_NO_INVOICE':
      return buildPath('expenses', null, { noinvoice: '1' });
    case 'CATEGORY_OVER_BASELINE':
      return buildPath('expenses', null, { cat: alert.entityId ?? '' });
    case 'RECEIPT_UNMATCHED':
    case 'CARD_CHARGE_SOON':
    case 'FORECAST_NEGATIVE':
      return buildPath('cashflow');
    case 'SUBSCRIPTION_CANCELLED':
      return buildPath('ops');
    default:
      return buildPath('settings', 'connections');
  }
}

// ───────────────────────────── sidebar ─────────────────────────────

export function Sidebar({ summary }: { summary: SummaryResponse | undefined }) {
  const { route } = useRouter();
  const { user, collapsed, toggleCollapsed, api, setUser } = useApp();
  const { navigate } = useRouter();
  const unread = summary?.alerts.unreadCount ?? 0;
  return (
    <aside className="sidebar" aria-label={he.nav.sidebar}>
      <div className="brand">
        <span className="logo" aria-hidden="true">
          {he.app.logoMark}
        </span>
        <div className="bt">
          <b>{he.app.name}</b>
          <span>{he.app.kind}</span>
        </div>
      </div>
      <nav className="nav" aria-label={he.nav.main}>
        {NAV.map((item) => (
          <Link key={item.screen} className="nav-item" to={buildPath(item.screen)} aria-current={route.screen === item.screen ? 'page' : undefined} title={item.label}>
            <Icon name={item.icon} />
            <span>{item.label}</span>
          </Link>
        ))}
        <div className="nav-sep" role="separator" />
        <Link className="nav-item" to={buildPath('alerts')} aria-current={route.screen === 'alerts' ? 'page' : undefined} title={he.nav.alerts}>
          <Icon name="bell" />
          <span>{he.nav.alerts}</span>
          {unread ? (
            <span className="cnt" aria-label={he.alerts.unreadCount(unread)}>
              {unread}
            </span>
          ) : null}
        </Link>
        <Link className="nav-item" to={buildPath('settings')} aria-current={route.screen === 'settings' ? 'page' : undefined} title={he.settings.title}>
          <Icon name="settings" />
          <span>{he.settings.title}</span>
        </Link>
      </nav>
      <button type="button" className="collapse-btn" aria-label={collapsed ? he.nav.expand : he.nav.collapse} aria-expanded={!collapsed} onClick={toggleCollapsed}>
        <Icon name="chevRight" />
        <span>{he.nav.collapse}</span>
      </button>
      <div className="nav-foot">
        <div className="nav-user">
          <span className="avatar" aria-hidden="true">
            {initial(user?.displayName ?? '')}
          </span>
          <span className="nu">
            {user?.displayName}
            <span className="ne">{user?.email}</span>
          </span>
        </div>
        <button
          type="button"
          className="nav-item"
          title={he.nav.logout}
          onClick={() => {
            void api.logout().catch(() => undefined);
            setUser(null);
            navigate(buildPath('login'));
          }}
        >
          <Icon name="logout" />
          <span>{he.nav.logout}</span>
        </button>
      </div>
    </aside>
  );
}

// ───────────────────────────── period picker ─────────────────────────────

function PeriodMenu({ summary }: { summary: SummaryResponse | undefined }) {
  const { period, setPeriod, closeLayer, toast } = useApp();
  const asOf = summary?.asOf ?? '2026-09-06';
  const months = periodMenuMonths(asOf, 13);
  const current = monthOf(asOf);
  return (
    <>
      {months.map((month) => {
        const selected = period === month || (period === 'month' && month === current) || (period === 'prev' && month === addMonths(current, -1));
        return (
          <button
            key={month}
            type="button"
            role="menuitemradio"
            aria-checked={selected}
            onClick={() => {
              setPeriod(month === current ? 'month' : (month as PeriodParam));
              closeLayer();
            }}
          >
            <Icon name="check" className="chk" />
            {month === current ? he.period.currentMonth(monthName(month)) : monthName(month)}
          </button>
        );
      })}
      <div className="sep" />
      <button
        type="button"
        onClick={() => {
          closeLayer();
          toast(he.period.customSoon);
        }}
      >
        <Icon name="calendar" className="chk" />
        {he.period.custom}
      </button>
    </>
  );
}

export function PeriodButton({ summary, compact }: { summary: SummaryResponse | undefined; compact?: boolean }) {
  const { openMenu, openSheet, period } = useApp();
  const isMobile = useIsMobile();
  const label = summary ? periodChipLabel(summary.period) : String(period);
  const open = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      openSheet({
        title: he.period.label,
        content: (
          <div className="popover menu" style={{ position: 'static', boxShadow: 'none', border: 0, padding: 0, maxHeight: 'none' }}>
            <PeriodMenu summary={summary} />
          </div>
        ),
      });
    } else {
      openMenu(event.currentTarget, <PeriodMenu summary={summary} />, { label: he.period.picker });
    }
  };
  if (compact)
    return (
      <button type="button" className="period-tap pv-only" aria-haspopup="menu" aria-label={`${he.period.picker}: ${label}`} onClick={open}>
        <span className="chip-btn">
          {label}
          <Icon name="chevDown" className="xs" />
        </span>
      </button>
    );
  return (
    <button type="button" className="btn sm period-btn" aria-haspopup="menu" aria-label={he.period.picker} onClick={open}>
      <Icon name="calendar" className="sm" />
      {label}
      <Icon name="chevDown" className="sm" />
    </button>
  );
}

// ───────────────────────────── refresh ─────────────────────────────

export function useRefreshDialog(summary: SummaryResponse | undefined) {
  const { openDialog, startSync } = useApp();
  const { navigate } = useRouter();
  return () => {
    const active = (summary?.connections ?? []).filter((c) => c.status === 'ACTIVE');
    if (summary && !active.length) {
      openDialog({
        title: he.refresh.title,
        text: he.refresh.noActive,
        actions: [
          { label: he.refresh.toConnections, tone: 'primary', onClick: () => navigate(buildPath('settings', 'connections')) },
          { label: he.app.cancel },
        ],
      });
      return;
    }
    const credits = summary?.credits;
    // The credits BALANCE is never shown — Financy exposes none (spec §3.4); only what we used.
    const text =
      credits && credits.plan && credits.usedThisMonth !== null
        ? he.refresh.bodyCredits(String(credits.usedThisMonth), String(credits.plan.credits))
        : he.refresh.body;
    openDialog({
      title: he.refresh.title,
      text,
      actions: [
        { label: he.refresh.confirm, tone: 'primary', onClick: () => startSync(true) },
        { label: he.app.cancel },
      ],
    });
  };
}

// ───────────────────────────── bell ─────────────────────────────

function BellPanelBody({ summary }: { summary: SummaryResponse | undefined }) {
  const { closeLayer, api } = useApp();
  const alerts = (summary?.alerts.items ?? []).filter((a) => !a.readAt).slice(0, 5);
  const shown = alerts.length ? alerts : (summary?.alerts.items ?? []).slice(0, 5);
  if (!shown.length) return <EmptyState good text={he.alerts.empty} />;
  return (
    <>
      {shown.map((alert) => (
        <Link
          key={alert.id}
          className={`mini-alert ${alert.severity}`}
          to={alertTarget(alert)}
          onClick={() => {
            closeLayer();
            void api.updateAlert(alert.id, { read: true }).catch(() => undefined);
          }}
        >
          <Icon name={ALERT_ICON[alert.type]} className="sm" />
          <span className="mt">{alert.title}</span>
          <span className="ma">{alert.actionLabel}</span>
        </Link>
      ))}
    </>
  );
}

function BellButton({ summary }: { summary: SummaryResponse | undefined }) {
  const { openPanel, closeLayer, api } = useApp();
  const { navigate } = useRouter();
  const isMobile = useIsMobile();
  const unread = summary?.alerts.unreadCount ?? 0;
  return (
    <button
      type="button"
      className="btn icon bell"
      aria-haspopup="dialog"
      aria-label={unread ? he.top.notificationsN(unread) : he.top.notifications}
      onClick={() => {
        if (isMobile) {
          navigate(buildPath('alerts'));
          return;
        }
        openPanel({
          title: he.alerts.panelTitle,
          content: <BellPanelBody summary={summary} />,
          footer: (
            <>
              <Link className="link-btn" to={buildPath('alerts')} onClick={closeLayer}>
                {he.alerts.panelAll} <Icon name="arrowLeft" />
              </Link>
              <button
                type="button"
                className="btn xs"
                onClick={() => {
                  closeLayer();
                  void api.readAllAlerts().catch(() => undefined);
                }}
              >
                {he.alerts.readAll}
              </button>
            </>
          ),
        });
      }}
    >
      <Icon name="bell" />
      {unread ? (
        <span className="badge" aria-hidden="true">
          {unread}
        </span>
      ) : null}
    </button>
  );
}

// ───────────────────────────── top bar ─────────────────────────────

export function Topbar({ summary, title, subtitle }: { summary: SummaryResponse | undefined; title: string; subtitle: string }) {
  const { route } = useRouter();
  const { user, openMenu, api, setUser, syncing, appearance, setAppearance } = useApp();
  const { navigate } = useRouter();
  const refresh = useRefreshDialog(summary);
  const fresh = freshnessView(summary);
  const unread = summary?.alerts.unreadCount ?? 0;

  return (
    <header className="topbar">
      <div className="tb-title">
        <h1>{title}</h1>
        <div className="sub">
          <span className="desk-only">{subtitle}</span>
          {PERIOD_SCREENS.has(route.screen) ? <PeriodButton summary={summary} compact /> : null}
          <span className="pv-only" style={{ fontSize: '12px', color: 'var(--text-3)' }}>
            {fresh.text}
          </span>
        </div>
      </div>
      <div className="tb-actions">
        {PERIOD_SCREENS.has(route.screen) ? <PeriodButton summary={summary} /> : null}
        <button type="button" className={`btn sm refresh-txt${syncing ? ' spin' : ''}`} aria-label={he.top.refreshAria} onClick={refresh}>
          <Icon name="refresh" className="sm" />
          {he.top.refresh}
          <span className={`updated${fresh.tone ? ' stale' : ''}`}> · {fresh.text}</span>
        </button>
        <button type="button" className={`btn icon pv-only${syncing ? ' spin' : ''}`} aria-label={he.top.refreshAria} onClick={refresh}>
          <Icon name="refresh" />
        </button>
        <BellButton summary={summary} />
        {api.isMock ? (
          <span className="demo-chip" title={he.app.demoTitle}>
            {he.app.demo}
          </span>
        ) : null}
        <button
          type="button"
          className="avatar-btn"
          aria-haspopup="menu"
          aria-label={he.top.userMenu(user?.displayName ?? '')}
          onClick={(event) =>
            openMenu(
              event.currentTarget,
              <UserMenu
                onLogout={() => {
                  void api.logout().catch(() => undefined);
                  setUser(null);
                  navigate(buildPath('login'));
                }}
                theme={appearance.theme}
                onTheme={(theme) => setAppearance({ theme })}
              />,
            )
          }
        >
          {initial(user?.displayName ?? '')}
        </button>
      </div>
      <span className="sr" role="status" aria-atomic="true">
        {unread ? he.alerts.unreadCount(unread) : he.alerts.noneNew}
      </span>
    </header>
  );
}

function UserMenu({ onLogout, theme, onTheme }: { onLogout: () => void; theme: 'light' | 'dark' | 'auto'; onTheme: (theme: 'light' | 'dark' | 'auto') => void }) {
  const { user, closeLayer } = useApp();
  return (
    <>
      <div className="hd">
        {user?.displayName} · <span className="ltr">{user?.email}</span>
      </div>
      <Link to={buildPath('settings', 'account')} onClick={closeLayer}>
        <Icon name="user" className="sm" />
        {he.settings.tabs.account}
      </Link>
      <div className="sep" />
      <div className="hd">{he.settings.theme}</div>
      {(
        [
          ['light', he.settings.themeLight, 'sun'],
          ['dark', he.settings.themeDark, 'moon'],
          ['auto', he.settings.themeAuto, 'laptop'],
        ] as const
      ).map(([key, label, icon]) => (
        <button key={key} type="button" role="menuitemradio" aria-checked={theme === key} onClick={() => onTheme(key)}>
          <Icon name="check" className="chk" />
          <Icon name={icon} className="sm" />
          {label}
        </button>
      ))}
      <div className="sep" />
      <button
        type="button"
        className="danger"
        onClick={() => {
          closeLayer();
          onLogout();
        }}
      >
        <Icon name="logout" className="sm" />
        {he.nav.logout}
      </button>
    </>
  );
}

// ───────────────────────────── bottom nav ─────────────────────────────

export function BottomNav({ summary }: { summary: SummaryResponse | undefined }) {
  const { route, navigate } = useRouter();
  const { openSheet, closeLayer, api, setUser, appearance, setAppearance } = useApp();
  const unread = summary?.alerts.unreadCount ?? 0;
  const moreActive = MORE_SCREENS.has(route.screen);
  return (
    <nav className="bottomnav" aria-label={he.nav.bottom}>
      {TABS.map((tab) => (
        <Link key={tab.screen} className="bn-item" to={buildPath(tab.screen)} aria-current={route.screen === tab.screen ? 'page' : undefined}>
          <Icon name={tab.icon} />
          <span>{tab.label}</span>
        </Link>
      ))}
      <button
        type="button"
        className="bn-item"
        aria-current={moreActive ? 'page' : undefined}
        aria-haspopup="dialog"
        onClick={() =>
          openSheet({
            title: he.nav.moreSheet,
            content: (
              <>
                <Link className="sheet-item" to={buildPath('expenses')} onClick={closeLayer}>
                  <Icon name="bag" />
                  {he.nav.expenses}
                </Link>
                <Link className="sheet-item" to={buildPath('loans')} onClick={closeLayer}>
                  <Icon name="landmark" />
                  {he.nav.loans}
                </Link>
                <Link className="sheet-item" to={buildPath('ops')} onClick={closeLayer}>
                  <Icon name="wrench" />
                  {he.nav.ops}
                </Link>
                <Link className="sheet-item" to={buildPath('alerts')} onClick={closeLayer}>
                  <Icon name="bell" />
                  {he.nav.alerts}
                  {unread ? <span className="cnt">{unread}</span> : null}
                </Link>
                <Link className="sheet-item" to={buildPath('settings')} onClick={closeLayer}>
                  <Icon name="settings" />
                  {he.settings.title}
                </Link>
                <hr className="divider" />
                <div className="setting-row">
                  <div className="sd">
                    <span>{he.nav.theme}</span>
                  </div>
                  <div className="sc">
                    <div className="seg" role="radiogroup" aria-label={he.nav.theme}>
                      {(
                        [
                          ['light', he.settings.themeLight],
                          ['dark', he.settings.themeDark],
                          ['auto', he.settings.themeAuto],
                        ] as const
                      ).map(([key, label]) => (
                        <button key={key} type="button" role="radio" aria-checked={appearance.theme === key} onClick={() => setAppearance({ theme: key })}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="sheet-item"
                  onClick={() => {
                    closeLayer();
                    void api.logout().catch(() => undefined);
                    setUser(null);
                    navigate(buildPath('login'));
                  }}
                >
                  <Icon name="logout" />
                  {he.nav.logout}
                </button>
              </>
            ),
          })
        }
      >
        <Icon name="grid" />
        <span>{he.nav.more}</span>
        {unread ? (
          <span className="badge" aria-hidden="true">
            {unread}
          </span>
        ) : null}
      </button>
    </nav>
  );
}

// ───────────────────────────── banners ─────────────────────────────

/**
 * The connection / freshness notice — REDESIGN §8: "The EXPIRED banner stops shouting. It becomes a
 * single amber line under the top bar with a `חבר מחדש` link, not a red panel above the content.
 * When the data is still recent, it lives in `אפשר לחכות` instead."
 *
 * So: nothing is rendered while the data is fresh (the home screen's `אפשר לחכות` bucket carries a
 * `connection_expired` row instead, per insight-contract §1.2), and when the data has actually
 * stopped arriving this is ONE amber line — provider mark, one sentence, one link.
 */
export function Banner({ summary }: { summary: SummaryResponse | undefined }) {
  const refresh = useRefreshDialog(summary);
  if (!summary) return null;

  const expired = summary.connections.filter((c) => c.needsRelink);
  const stale = summary.freshness.level === 'stale_warn' || summary.freshness.level === 'stale_critical' || summary.freshness.level === 'none';

  if (expired.length && stale) {
    const first = expired[0]!;
    return (
      <div className="fk-line-notice" role="status">
        <ProviderMark providerId={first.providerId} size={20} />
        <span className="fk-line-notice__t">
          {he.banner.expiredTitle(providerName(first.providerId))} {he.banner.expiredBody(fmtDate(first.lastFetchedAt?.slice(0, 10) ?? summary.asOf))}
        </span>
        <a className="link-btn" href="https://app.open-finance.ai" target="_blank" rel="noreferrer noopener">
          {he.banner.reconnect}
          <Icon name="external" className="sm" />
        </a>
      </div>
    );
  }

  if (stale) {
    return (
      <div className="fk-line-notice" role="status">
        <Icon name="clock" className="sm" />
        <span className="fk-line-notice__t">
          {he.banner.staleTitle(fmtDateTime(summary.freshness.lastFetchedAt))} {he.banner.staleBody}
        </span>
        <button type="button" className="link-btn" onClick={refresh}>
          {he.top.refreshShort}
          <Icon name="refresh" className="sm" />
        </button>
      </div>
    );
  }

  return null;
}

/** The account label used by the hero and the transactions filter. */
export function accountLabel(summary: SummaryResponse | undefined): ReactNode {
  const last4 = summary?.balance.account?.numberLast4 ?? null;
  return <Mask>{mask(last4)}</Mask>;
}

/** A generic "day N of M" progress bar (spec §4.1 A2). */
export function MonthProgress({ day, total }: { day: number; total: number }) {
  return (
    <div className="stack" style={{ gap: '4px' }}>
      <div className="progress" aria-hidden="true">
        <i style={{ width: `${Math.round((day / total) * 100)}%` }} />
      </div>
      <div className="note">
        {he.app.day} <Num>{day}</Num> {he.app.of} <Num>{total}</Num>
      </div>
    </div>
  );
}
